import feathers from 'utility/client'
import {useContext, useEffect, useMemo, useReducer, useState} from 'react'
import {useParams} from 'react-router-dom'
import {SearchBarContext} from 'components/search-bar/SearchBarContext'
import {Pagination} from 'components/pagination/Pagination'
import {forEach, isArray, isObject, has} from 'lodash'
import {Loader} from './Loader'
import {Placeholder} from './Placeholder'
import {ListReducer, ListReducerActions} from './reducer'
import { useLogger } from 'hooks/useLogger'

const configPrototype = {
  query: {},
  paramsAsQueryParams: [],
  searchProperty: 'name'
}

export const connectService = (Component, service, config = configPrototype) => {
  return props => {
    // Allow override search context incase component is out of
    // global search context
    const {SearchContext = SearchBarContext} = props
    const logger = useLogger()

    // Generic searchbar context
    const {search} = useContext(SearchContext)
    // Searchable property filter name
    const {searchProperty = 'name', paramsAsQueryParams = []} = config

    const routeParams = useParams()

    // Current Page
    const [page, setPage] = useState(1)
    const [skip, setSkip] = useState(0)
    const [limit, setLimit] = useState(10)
    const [total, setTotal] = useState(0)

    const [data, setData] = useState({})

    const [list, dispatch] = useReducer(ListReducer, [])

    const [loading, setLoading] = useState(false)

    const onCreated = data => {
      logger.log('Created', data)
      if (!shouldActOnEvent(data)) return
      dispatch({type: ListReducerActions.CREATE, data})
    }

    const onPatched = data => {
      logger.log('Patched', data)
      if (!shouldActOnEvent(data)) return
      dispatch({type: ListReducerActions.UPDATE, data})
    }

    const onRemoved = data => {
      logger.log('Removed', data)
      if (!shouldActOnEvent(data)) return
      dispatch({type: ListReducerActions.REMOVE, data})
    }

    useEffect(() => {
      const _service = feathers.service(service)
      _service.on('created', onCreated)
      _service.on('patched', onPatched)
      _service.on('updated', onPatched)
      _service.on('removed', onRemoved)

      return () => {
        _service.removeListener('created', onCreated)
        _service.removeListener('patched', onPatched)
        _service.removeListener('updated', onPatched)
        _service.removeListener('removed', onRemoved)
      }
    }, [])

    const shouldActOnEvent = data => {
      for (const key in forwardedParams) {
        if (!has(data, key)) {
          return false
        }

        if (isObject(data[key])) {
          if (data[key]?._id !== forwardedParams[key]) {
            return false
          }
        } else if (data[key] !== forwardedParams[key]) {
          return false
        }

      }
      return true
    }

    const forwardedParams = useMemo(() => {
      const _queryParams = {}
      forEach(paramsAsQueryParams, name => {
        if (routeParams[name]) _queryParams[name] = routeParams[name]
      })
      return _queryParams
    }, [paramsAsQueryParams, routeParams])

    const defaultQuery = useMemo(() => {
      return config?.query || props?.query || {}
    }, [props.query])

    const find = async _query => {
      const query = {
        ..._query,
        ...forwardedParams,
        ...defaultQuery
      }

      setLoading(true)
      logger.log('find', query)

      const result = await feathers.service(service).find({query})
      logger.log(result)
      if (query?.$limit === -1) {
        dispatch({type: ListReducerActions.HYDRATE, data: result})
        setTotal(result.length)
      } else {
        dispatch({type: ListReducerActions.HYDRATE, data: result.data})
        setTotal(result.total)
      }
      setLoading(false)
    }

    const get = async id => {
      setLoading(true)
      const result = await feathers.service(service).get(id, {
        query: {
          ...forwardedParams,
          ...defaultQuery
        }
      })
      setData(result)
      setLoading(false)
    }

    const create = (data, validator) => {
      return feathers.service(service).create(data)
    }

    const patch = (id, data, validator) => {
      return feathers.service(service).patch(id, data)
    }

    const remove = id => {
      return feathers.service(service).remove(id)
    }

    /**
     * @description params[service] will be considered as update
     * or view mode id here in route /:distributor/reseller/:reseller
     * const id = params['distributor'] will be considered as component
     * is connected to distributor service.
     *
     */
    const params = useParams()

    const isEditing = useMemo(() => {
      /**
       * @description When connect service binds to some service and
       * route contains the parameter named to same service which is
       * not equal to 'create', it's the entity being edited.
       *
       * i.e. for example, component is bind to distributor service and
       * route param contains distributor parameter having objectId, then
       * isEditing will be true
       */
      return params[service] && params[service] !== 'create'
    }, [params])

    const rehydrate = () => {
      if (isEditing) {
        get(params[service])
      } else {
        const query = {
          $limit: limit,
          $skip: skip,
          ...forwardedParams
        }

        if (search) {
          if (isArray(searchProperty)) {
            const $or = []
            forEach(searchProperty, property => {
              $or.push({
                [property]: {
                  $regex: search,
                  $options: 'i'
                }
              })
            })

            query.$or = $or
          } else {
            query[searchProperty] = {}
            query[searchProperty]['$regex'] = search
            query[searchProperty]['$options'] = 'i'
          }
        }
        find(query)
      }
    }

    useEffect(() => {
      rehydrate()
    }, [limit, params, search, searchProperty, skip])

    useEffect(() => {
      setSkip((page - 1) * limit)
    }, [limit, page])

    const onSave = async ({data: _data = false, beforeSave = arg => arg} = {}) => {
      const bind = {}
      forEach(paramsAsQueryParams, name => {
        if (routeParams[name]) bind[name] = routeParams[name]
      })

      logger.log({data, _data, bind})

      if (isEditing) {
        const {_id, __v, ...payload} = _data ? _data : data
        return await patch(params[service], beforeSave({...payload, ...bind}))
      } else {
        return await create(beforeSave(_data ? _data : {...data, ...bind}))
      }
    }

    const Paginator = additionalProps => {
      return <Pagination total={total} limit={limit} page={page} setPage={setPage} {...additionalProps} />
    }

    return (
      <>
        <Component
          edit={isEditing}
          page={page}
          setPage={setPage}
          limit={limit}
          total={total}
          skip={skip}
          setLimit={setLimit}
          find={find}
          rehydrate={rehydrate}
          create={create}
          patch={patch}
          get={get}
          remove={remove}
          loading={loading}
          list={list}
          data={data}
          onSave={onSave}
          setData={setData}
          Loader={Loader}
          Placeholder={Placeholder}
          Paginator={Paginator}
          onCreated={onCreated}
          {...props}
        />
      </>
    )
  }
}
